<template>
  <div class="max-width-600">
    <form
      autocomplete="off"
      @submit.prevent="handleSubmit"
    >
      <AppCard>
        <PasswordInput
          v-model="formData.currentPassword"
          label="Your current password"
          :validation-errors="validationErrors?.currentPassword"
          required
        />

        <PasswordInput
          v-model="formData.newPassword"
          label="Your new password"
          :validation-errors="validationErrors?.newPassword"
          required
        />

        <PasswordInput
          v-model="formData.confirmPassword"
          label="Confirm your new password"
          :validation-errors="validationErrors?.confirmPassword"
          required
        />

        <template #buttons>
          <ButtonSubmitRow
            :processing="isSubmitting"
            :cancel-route="{ path: router.options.history.state.back }"
          >
            {{ isSubmitting ? 'Saving...' : 'Save' }}
          </ButtonSubmitRow>
        </template>
      </AppCard>
    </form>
  </div>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import { AppCard } from '@/components/cards';
import { ButtonSubmitRow } from '@/components/buttons';
import { PasswordInput } from '@/components/form-inputs';

import { useHttp, useToasts } from '@/composables';
import { useAuthStore } from '@/store';

const authStore = useAuthStore();
const router = useRouter();
const toasts = useToasts();

const formData = reactive({
  currentPassword: null,
  newPassword: null,
  confirmPassword: null,
});

const isSubmitting = ref(false);
const validationErrors = reactive({
  currentPassword: null,
  newPassword: null,
  confirmPassword: null,
});

const handleSubmit = async () => {
  isSubmitting.value = true;

  // Clear previous errors
  validationErrors.currentPassword = null;
  validationErrors.newPassword = null;
  validationErrors.confirmPassword = null;

  // Client-side validation for confirm password, because this isn't validated on the server
  if (!formData.confirmPassword) {
    validationErrors.confirmPassword = ['This field is required'];
  } else if (formData.confirmPassword !== formData.newPassword) {
    validationErrors.confirmPassword = ['The confirm password does not match new password.'];
  }

  if (!validationErrors.confirmPassword) {
    const response = await useHttp().post(
      '/auth/password-change',
      { currentPassword: formData.currentPassword, newPassword: formData.newPassword },
    );

    if (response.statusCode === HttpStatusCode.Ok) {
      toasts.success('Your password has been successfully changed.');

      const logoutResponse = await useHttp().post('/auth/logout');

      if (logoutResponse.statusCode === HttpStatusCode.Ok) {
        authStore.user = null;
        await router.push({ name: 'app_auth_login' });
      }
    } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
      const errors = response.errors;

      if (errors.currentPassword) {
        validationErrors.currentPassword = errors.currentPassword;
      }

      if (errors.newPassword) {
        validationErrors.newPassword = errors.newPassword;
      }
    }
  }
  isSubmitting.value = false;
};

</script>
